import React, { useState, useEffect } from "react";
import './OpalPlanningLayout.css';
import PlanningView from "./PlanningView/PlanningView";
import PlanningSidebar from "./PlanningSidebar/PlanningSidebar";
import { addRecord, getAll, getNextId, getRecordById, updateRecord } from "../../localDB";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Nav, Tab } from "react-bootstrap";
import TauriImage from "../TauriImage/TauriImage";
import AccountsView from "./AccountView/AccountView";
import appURL from '../../appURL.json';
import TauriLoader2 from "../TauriLoader2/TauriLoader2";
import { useBaseProvider } from "../../BaseManager/BaseManager";
import OpalAlertModal from "../OpalAlertModal/OpalAlertModal";
import AddSchedule from "./AddSchedule/AddSchedule";
import StatisticsSidebar from "./StatisticsSidebar/StatisticsSidebar";
import { activityClassification, eventTypes, planStatusDisplayNames, planWorksheetStatusMapping } from "../../visualizations/visualizations";
import SubmissionErrorModal from "./SubmissionErrorModal/SubmissionErrorModal";
import AddActivity from "./AddActivity/AddActivity";
import AddLeave from "./AddLeave/AddLeave";
import AddWorkWithModal from "./AddWorkWithModal/AddWorkWithModal";


const statisticsTemplate = [
    {
        "name": "Cycle Period",
        "icon": "/Images/fe--calendar.svg",
        "details": [
            {
                "name": "Range",
                "count": "",
                "highlight": null
            },
            {
                "name": "Working Days",
                "count": 0
            },
            {
                "name": "Holidays",
                "count": 0
            }
        ]
    },
    {
        "name": "Plotting",
        "details": []
    },
    {
        "name": "Accounts",
        "details": [
            {
                "name": "Plotted",
                "count": 0,
                "highlight": null
            },
            {
                "name": "Target",
                "count": 0
            },
            {
                "name": "Remaining",
                "count": 0
            }
        ]
    },
    {
        "name": "Activity Types",
        "details": []
    },
    {
        "name": "Frequencies",
        "details": []
    },
    {
        "name": "Events",
        "icon": "/Images/mdi--calendar-star.svg",
        "details": []
    }
]

const OpalPlanningLayout = () => {

    const { id } = useParams();

    const { isSync } = useBaseProvider();

    const [selectedDate, setSelectedDate] = useState(null);
    const [worksheet, setWorksheet] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [holidays, setHolidays] = useState([]);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [activeTab, setActiveTab] = useState("scheduling");
    const [creatingSchedule, setCreatingSchedule] = useState(false);
    const [deletingSchedule, setDeletingSchedule] = useState(false);
    const [submittingSchedule, setSubmittingSchedule] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [loadingScheduling, setLoadingScheduling] = useState(false);
    const [showAddSchedule, setShowAddSchedule] = useState(false);
    const [showAddActivity, setShowAddActivity] = useState(false);
    const [showAddLeave, setShowAddLeave] = useState(false);
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertModalProps, setAlertModalProps] = useState(null);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [showSubmissionErrorModal, setShowSubmissionErrorModal] = useState(false);
    const [validaionError, setValidationError] = useState(null);
    const [statistics, setStatistics] = useState(statisticsTemplate);
    const [policies, setPolicies] = useState([]);
    const [showAddWorkWith, setShowAddWorkWith] = useState(false);
    const [workWithEmployees, setWorkWithEmployees] = useState([]);

    const navigate = useNavigate();

    const storedPermission = localStorage.getItem("permission");
    const permission = JSON.parse(atob(storedPermission));

    const applyStatisticsUpdates = (baseStatistics, updates) => {
        // First, update existing sections
        const updatedBase = baseStatistics.map(section => {
            const matchingSection = updates.find(u => u.name === section.name);
            if (!matchingSection) return section; // keep original if no match

            return {
                ...section,
                details: Array.isArray(matchingSection.details)
                    ? matchingSection.details
                    : []
            };


        });

        // Add new sections that don’t exist in baseStatistics
        const newSections = updates.filter(
            u => !baseStatistics.some(bs => bs.name === u.name)
        );

        return [...updatedBase, ...newSections];
    };


    const generateStatistics = async (wsheet) => {
        if (!wsheet) return [];
        const days = wsheet.Days || {};

        const dailyMinPolicy = policies.find(p => p.name === "workplan.policies.daily_minimum");
        const targetPolicy = policies.find(p => p.name === "workplan.policies.plan_minimum");

        const dailyMin = dailyMinPolicy?.value || 0;
        // const target = targetPolicy?.value || 0;

        const target =
            wsheet?.Statistics
                ?.find(a => a.name === "Accounts")
                ?.details?.find(d => d.name === "Target")
                ?.count ?? 0;

        let completed = 0;
        let totalAccounts = 0;

        for (const date in days) {
            const count = days[date].accounts?.length || 0;
            totalAccounts += count;
            if (count >= dailyMin) completed += 1;
        }

        const cycleStats =
            wsheet?.Statistics?.find(s => s.name === "Cycle Period")?.details || [];

        const sections = [
            {
                name: "Cycle Period",
                Icon: "/Images/fe--calendar.svg",
                details: [
                    {
                        name: "Range",
                        count: wsheet?.Cycle?.Description || "N/A",
                        highlight: wsheet?.Cycle?.Code || null
                    },
                    ...cycleStats
                ]
            },
            {
                name: "Plotting",
                details: wsheet?.Statistics?.find(a => a.name === "Plotting")?.details || [],
            },
            {
                name: "Accounts",
                details: wsheet?.Statistics?.find(a => a.name === "Accounts")?.details || []
            },
            {
                name: "Activity Types",
                details: wsheet?.Statistics?.find(a => a.name === "Activity Types")?.details || []
            },
            {
                name: "Frequencies",
                details: wsheet?.Statistics?.find(a => a.name === "Frequencies")?.details || []
            },
            {
                name: "Events",
                details: wsheet?.Statistics?.find(a => a.name === "Events")?.details || []
            }
        ];

        const accountsSection = sections.find(s => s.name === "Accounts");

        if (accountsSection?.details) {
            const plotted = accountsSection.details.find(d => d.name === "Plotted");

            if (plotted && target > 0) {
                plotted.highlight = `(${Math.round((totalAccounts / target) * 100)}%)`;
            }
        }


        setStatistics(applyStatisticsUpdates(statistics, sections));
    }

    const selectedItem = async (item) => {
        const isManager = Number(permission?.coverage?.employee_id) !== Number(worksheet?.EmployeeID);
        if (isSync || creatingSchedule || isManager || ![planWorksheetStatusMapping.ForSubmission, planWorksheetStatusMapping.UpdateRequired].includes(worksheet?.Status)) return;
        setSelectedAccount(item);
        setShowAddSchedule(true);
    }

    const onDeleteItem = async (accountID, date) => {
        const isManager = Number(permission?.coverage?.employee_id) !== Number(worksheet?.EmployeeID);
        if (isSync || deletingSchedule || isManager || ![planWorksheetStatusMapping.ForSubmission, planWorksheetStatusMapping.UpdateRequired].includes(worksheet?.Status)) return;
        try {
            setShowLoadingModal(true);
            await removeAccountToDate(accountID, date);
            await fetchData(Number(id));
        } catch (err) {
            console.log(err);
        } finally {
            setShowLoadingModal(false);
        }
    }

    const onDeleteActivity = async (event) => {
        const isManager = Number(permission?.coverage?.employee_id) !== Number(worksheet?.EmployeeID);
        if (isSync || deletingSchedule || isManager || ![planWorksheetStatusMapping.ForSubmission, planWorksheetStatusMapping.UpdateRequired].includes(worksheet?.Status)) return;
        try {
            const payload = {
                Date: selectedDate ? selectedDate.toLocaleDateString("en-CA") : null,
                EventID: event?.referenceID,
                Type: event?.type
            }
            setShowLoadingModal(true);
            await removeActivityToDate(payload);
            await fetchData(Number(id));
        } catch (err) {
            console.log(err);
        } finally {
            setShowLoadingModal(false);
        }
    }


    const handleDateChange = (date) => {
        setSelectedDate(date);
        const r_sidebar = document.getElementById("rightSidebar");

        if (r_sidebar) r_sidebar.classList.add("show");

        setSidebarVisible(true);
    };

    const handleSubmitClick = () => {
        setAlertModalProps({
            title: "Confirm Submission",
            bodyText: "Are you sure you want to submit this plan?",
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            modalType: "confirm",
            onConfirm: async () => {
                setShowAlertModal(false);
                await onSubmit();
            }
        });

        setShowAlertModal(true);
    };

    const onSubmit = async () => {
        setSubmittingSchedule(true);
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(id)}/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {
                try {
                    let errorData = null;
                    try {
                        errorData = await response.json();
                    } catch {
                        errorData = {};
                    }


                    if (errorData?.error || errorData?.description) {
                        const modalConfig = {
                            title: "Message",
                            bodyText: errorData?.error?.description || errorData?.description || "Submit failed!",
                            confirmButtonText: "OK",
                            cancelButtonText: "",
                            modalType: "message"
                        };

                        setAlertModalProps(modalConfig);
                        setShowAlertModal(true);
                    }

                    if (
                        errorData?.validation?.stoppers != null ||
                        errorData?.validation?.warnings != null
                    ) {
                        setValidationError(errorData?.validation);
                        setShowSubmissionErrorModal(true);
                    }


                } catch (err) {
                    console.error("Failed to parse error response:", err);
                }
                return;
            }

            const successModalConfig = {
                title: "Successful",
                bodyText: "Your coverage plan has been submitted for approval",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message",
                Icon: "/Images/material-symbols--check-plan.svg"
            };


            await fetchData(Number(id));
            setAlertModalProps(successModalConfig);
            setShowAlertModal(true);
        } catch (err) {
            const modalConfig = {
                title: "Message",
                bodyText: "Submit failed!",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message"
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
        } finally {
            setSubmittingSchedule(false);
        }
    }

    const onApprove = async () => {
        if (!permission?.planning?.approve_plan) {
            const modalConfig = {
                title: "Message",
                bodyText: "You don't have the permission to do this action.",
                confirmButtonText: "Ok",
                cancelButtonText: "",
                modalType: "message"
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
            return;
        }

        setIsApproving(true);
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(id)}/approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {
                try {
                    let errorData = null;
                    try {
                        errorData = await response.json();
                    } catch {
                        errorData = {};
                    }


                    if (errorData?.error || errorData?.description) {
                        const modalConfig = {
                            title: "Message",
                            bodyText: errorData?.error?.description || errorData?.description || "Approve failed!",
                            confirmButtonText: "OK",
                            cancelButtonText: "",
                            modalType: "message"
                        };

                        setAlertModalProps(modalConfig);
                        setShowAlertModal(true);
                    }

                    if (
                        errorData?.validation?.stoppers != null ||
                        errorData?.validation?.warnings != null
                    ) {
                        setValidationError(errorData?.validation);
                        setShowSubmissionErrorModal(true);
                    }


                } catch (err) {
                    console.error("Failed to parse error response:", err);
                }
                return;
            }

            const updateWorkPlan = {
                ...worksheet,
                Status: planWorksheetStatusMapping.Approved
            }

            setWorksheet(updateWorkPlan);
            updateRecord("Worksheets", updateWorkPlan);

            const successModalConfig = {
                title: "PLAN APPROVED",
                bodyText: `Your coverage plan for ${worksheet?.Cycle?.Code} has been approved.`,
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message",
                Icon: "/Images/material-symbols--check-plan.svg"
            };

            setAlertModalProps(successModalConfig);
            setShowAlertModal(true);
        } catch (err) {
            const modalConfig = {
                title: "Message",
                bodyText: "Approve failed!",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message"
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
        } finally {
            setIsApproving(false);
        }
    }

    const addAccountToDate = async (payload, activityClass) => {
        setCreatingSchedule(true);
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(id)}/add/${activityClass?.Classification === activityClassification.System
                ? "regular"
                : "contextual"
                }`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    [activityClass?.Classification === activityClassification.System
                        ? 'X-ETR-Device-ID'   // default when System
                        : 'X-ETR-DeviceID'    // alternative for others
                    ]: devId
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                try {
                    let errorData = null;
                    try {
                        errorData = await response.json();
                    } catch {
                        errorData = {};
                    }

                    const modalConfig = {
                        title: "Submission Error",
                        bodyText: errorData?.description || "Error creating schedule",
                        confirmButtonText: "OK",
                        cancelButtonText: "",
                        modalType: "message"
                    };

                    setAlertModalProps(modalConfig);
                    setShowAlertModal(true);
                } catch (err) {
                    console.error("Failed to parse error response:", err);
                }
                return;
            }

            const result = await response.json();
            setStatistics(applyStatisticsUpdates(statistics, result.statistics));

            setAlertModalProps(null);
            setShowAddSchedule(false);
            await fetchData(Number(id));
        } catch (err) {

            const modalConfig = {
                title: "Submission Error",
                bodyText: "Error creating schedule",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message"
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
        } finally {
            setCreatingSchedule(false);
        }
    }

    const addAccountToMultipleDate = async (payloads, activityClass) => {
        setCreatingSchedule(true);
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");

            let lastResult = null;

            // loop through each payload in the array
            for (const payload of payloads) {
                const response = await fetch(`${appURL.API_Worksheet}/${Number(id)}/add/${activityClass?.Classification === activityClassification.System
                    ? "regular"
                    : "contextual"
                    }`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        [activityClass?.Classification === activityClassification.System
                            ? 'X-ETR-Device-ID'   // default when System
                            : 'X-ETR-DeviceID'    // alternative for others
                        ]: devId
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    let errorData = null;
                    try {
                        errorData = await response.json();
                    } catch {
                        errorData = {};
                    }

                    const modalConfig = {
                        title: "Submission Error",
                        bodyText: errorData?.description || "Error creating schedule",
                        confirmButtonText: "OK",
                        cancelButtonText: "",
                        modalType: "message",
                    };

                    setAlertModalProps(modalConfig);
                    setShowAlertModal(true);
                    return; // stop loop on first error
                }

                lastResult = await response.json();

            }
            setStatistics(applyStatisticsUpdates(statistics, lastResult.statistics));
            setAlertModalProps(null);
            setShowAddSchedule(false);
            await fetchData(Number(id));
        } catch (err) {
            const modalConfig = {
                title: "Submission Error",
                bodyText: "Error creating schedule",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message",
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
        } finally {
            setCreatingSchedule(false);
        }
    };

    const removeAccountToDate = async (accountID, date) => {
        setDeletingSchedule(true);
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(id)}/${date}/account/${accountID}/remove`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {
                try {
                    let errorData = null;
                    try {
                        errorData = await response.json();
                    } catch {
                        errorData = {};
                    }

                    const modalConfig = {
                        title: "Message",
                        bodyText: errorData?.description || "Error creating schedule",
                        confirmButtonText: "OK",
                        cancelButtonText: "",
                        modalType: "message"
                    };

                    setAlertModalProps(modalConfig);
                    setShowAlertModal(true);
                } catch (err) {
                    console.error("Failed to parse error response:", err);
                }
                return;
            }

            const result = await response.json();
            setStatistics(applyStatisticsUpdates(statistics, result.statistics));

            setAlertModalProps(null);
        } catch (err) {

            const modalConfig = {
                title: "Submission Error",
                bodyText: "Error creating schedule",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message"
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
        } finally {
            setDeletingSchedule(false);
        }
    }

    const addActivityToDate = async (payload, type) => {
        setCreatingSchedule(true);
        try {
            const endpoint = (
                type === eventTypes.Recurring
                    ? "recurring"
                    : type === eventTypes.Organizational
                        ? "organizational"
                        : ""
            );

            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(id)}/add/${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                try {
                    let errorData = null;
                    try {
                        errorData = await response.json();
                    } catch {
                        errorData = {};
                    }

                    const modalConfig = {
                        title: "Submission Error",
                        bodyText: errorData?.description || "Error creating activity",
                        confirmButtonText: "OK",
                        cancelButtonText: "",
                        modalType: "message"
                    };

                    setAlertModalProps(modalConfig);
                    setShowAlertModal(true);
                } catch (err) {
                    console.error("Failed to parse error response:", err);
                }
                return;
            }

            const result = await response.json();
            setStatistics(applyStatisticsUpdates(statistics, result.statistics));

            setAlertModalProps(null);
            setShowAddActivity(false);
            await fetchData(Number(id));
        } catch (err) {

            const modalConfig = {
                title: "Submission Error",
                bodyText: "Error creating schedule",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message"
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
        } finally {
            setCreatingSchedule(false);
        }
    }

    const removeActivityToDate = async (payload) => {
        setDeletingSchedule(true);
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");

            const response = await fetch(`${appURL.API_Worksheet}/${Number(id)}/remove/event`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                try {
                    let errorData = null;
                    try {
                        errorData = await response.json();
                    } catch {
                        errorData = {};
                    }

                    const modalConfig = {
                        title: "Submission Error",
                        bodyText: errorData?.description || "Failed to remove activity",
                        confirmButtonText: "OK",
                        cancelButtonText: "",
                        modalType: "message"
                    };

                    setAlertModalProps(modalConfig);
                    setShowAlertModal(true);
                } catch (err) {
                    console.error("Failed to parse error response:", err);
                }
                return;
            }

            const result = await response.json();
            setStatistics(applyStatisticsUpdates(statistics, result.statistics));

            setAlertModalProps(null);
        } catch (err) {

            const modalConfig = {
                title: "Submission Error",
                bodyText: "Failed to remove activity",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message"
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
        } finally {
            setDeletingSchedule(false);
        }
    }

    const addLeaveToDate = async (payload) => {
        setCreatingSchedule(true);
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(id)}/add/leave`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                try {
                    let errorData = null;
                    try {
                        errorData = await response.json();
                    } catch {
                        errorData = {};
                    }

                    const modalConfig = {
                        title: "Submission Error",
                        bodyText: errorData?.description || "Error creating leave",
                        confirmButtonText: "OK",
                        cancelButtonText: "",
                        modalType: "message"
                    };

                    setAlertModalProps(modalConfig);
                    setShowAlertModal(true);
                } catch (err) {
                    console.error("Failed to parse error response:", err);
                }
                return;
            }

            const result = await response.json();
            setStatistics(applyStatisticsUpdates(statistics, result.statistics));

            setAlertModalProps(null);
            setShowAddLeave(false);
            await fetchData(Number(id));
        } catch (err) {

            const modalConfig = {
                title: "Submission Error",
                bodyText: "Error creating leave",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message"
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
        } finally {
            setCreatingSchedule(false);
        }
    }

    const addWorkWith = async (payload) => {
        setCreatingSchedule(true);
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(id)}/workwith`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                try {
                    let errorData = null;
                    try {
                        errorData = await response.json();
                    } catch {
                        errorData = {};
                    }

                    const modalConfig = {
                        title: "Submission Error",
                        bodyText: errorData?.description || "Error modify work with",
                        confirmButtonText: "OK",
                        cancelButtonText: "",
                        modalType: "message"
                    };

                    setAlertModalProps(modalConfig);
                    setShowAlertModal(true);
                } catch (err) {
                    console.error("Failed to parse error response:", err);
                }
                return;
            }

            const result = await response.json();
            setStatistics(applyStatisticsUpdates(statistics, result.statistics));

            setAlertModalProps(null);
            setShowAddWorkWith(false);
            await fetchData(Number(id));
        } catch (err) {

            const modalConfig = {
                title: "Submission Error",
                bodyText: "Error modify work with",
                confirmButtonText: "OK",
                cancelButtonText: "",
                modalType: "message"
            };

            setAlertModalProps(modalConfig);
            setShowAlertModal(true);
        } finally {
            setCreatingSchedule(false);
        }
    }

    const fetchOnlineUserWorksheet = async (w_id) => {
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(permission?.coverage?.employee_id)}/worksheets/${w_id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {
                console.log("Failed to fetch worksheet!");
                return;
            }

            const data = await response.json();
            const worksheet = await getRecordById("Worksheets", w_id)
            const updatePlanDays = {
                ...worksheet,
                Statistics: data?.plan.statistics,
                Status: planWorksheetStatusMapping[data?.plan?.status],
                Days: data?.plan?.days
            }
            setWorksheet(updatePlanDays);
            await updateRecord("Worksheets", updatePlanDays);

        } catch (err) {
            console.log(err);
        }
    }

    const fetchOfflineUserWorksheet = async (w_id) => {
        try {
            const plan = await getRecordById("Worksheets", Number(w_id));
            setWorksheet(plan);

        } catch (err) {
            console.log(err);
        }
    }

    const fetchData = async (w_id) => {
        const isOnline = JSON.parse(localStorage.getItem("isOnline"));
        const hasInternet = navigator.onLine;

        if (isOnline && hasInternet) {
            await fetchOnlineUserWorksheet(w_id);
            await fetchOnlineSubordinates();
        } else {
            await fetchOfflineUserWorksheet(w_id);
        }
    }

    const fetchOnlineSubordinates = async () => {
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const response = await fetch(`${appURL.API_Worksheet}/${Number(permission?.coverage?.employee_id)}/subordinates`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {
                console.log("Failed to fetch employees!");
                return;
            }

            const data = await response.json();
            setWorkWithEmployees(data?.employees);
        } catch (err) {
            console.log(err);
        }
    }

    const loadInitialCycle = async (w_id) => {
        const plan = await getRecordById("Worksheets", w_id);

        if (plan?.Cycle?.StartDate) {
            const startDate = new Date(plan?.Cycle?.StartDate);
            startDate.setHours(0, 0, 0, 0);
            setSelectedDate(startDate);
        }
    }

    useEffect(() => {
        const loadCycle = async () => {
            try {
                setLoadingScheduling(true);
                if (!id) return;

                const _holiday = await getAll("holiday");
                const _policies = await getRecordById("Policies", Number(localStorage.getItem("usr_g")));
                setHolidays(_holiday);
                setPolicies(_policies.Policies);
                await fetchData(Number(id));
                await loadInitialCycle(Number(id));
            } catch (err) {
                console.log(err)
            } finally {
                setLoadingScheduling(false);
            }
        };

        loadCycle();

    }, [id]);

    useEffect(() => {
        generateStatistics(worksheet);
    }, [worksheet])

    return (
        <div className="opal-layout-container d-flex h-100 row">
            <Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
                <div className="panel-top">
                    <Button className='btn-back d-flex align-items-center' onClick={() => navigate(-1)}>
                        <TauriImage localImage="/Images/material-symbols--arrow-back.svg" alt="arrow-down" className="dt-arrowdown" color={"#000"} />
                        <span className="ms-2 text-nowrap">Back</span>
                    </Button>
                    <Nav variant="tabs">
                        <Nav.Item>
                            <Nav.Link eventKey="account">
                                <TauriImage localImage="/Images/mdi--users.svg" alt="icon" className="dt-arrowdown" color={"#000"} />
                                <span>Accounts Management</span>
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="scheduling">
                                <TauriImage localImage="/Images/fe--calendar.svg" alt="icon" className="dt-arrowdown" color={"#000"} />
                                <span>Scheduling</span>
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </div>

                <Tab.Content className="p-0">
                    <Tab.Pane transition={false} eventKey="account">
                        <div className="account-container d-flex justify-content-center">
                            <AccountsView id={worksheet?.Cycle?.ID} />
                        </div>
                    </Tab.Pane>

                    <Tab.Pane transition={false} eventKey="scheduling">
                        {loadingScheduling ? (
                            <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
                        ) : (
                            <div className="d-flex w-100 h-100">
                                <div id="leftSidebar" className="col-2 p-0">
                                    <StatisticsSidebar statisticData={statistics} />
                                </div>
                                <main className={`${sidebarVisible ? "col-8 p-0 m-0" : "flex-grow-1 col-9 p-0 m-0"}`}>
                                    <div className="host-container" tag="main">
                                        <div className="flex-grow-1">
                                            {selectedDate && (
                                                <PlanningView
                                                    selectedDate={selectedDate}
                                                    setSelectedDate={handleDateChange}
                                                    worksheet={worksheet}
                                                    policies={policies}
                                                    holidays={holidays}
                                                    onSubmit={handleSubmitClick}
                                                    onApprove={onApprove}
                                                    isSubmitting={submittingSchedule}
                                                    isApproving={isApproving}
                                                    canApprove={permission?.planning?.approve_plan}
                                                    canJoinCall={permission?.coverage?.joint_calls}
                                                    isManager={Number(permission?.coverage?.employee_id) !== Number(worksheet?.EmployeeID)}
                                                    addActivityClick={() => {
                                                        const isManager = Number(permission?.coverage?.employee_id) !== Number(worksheet?.EmployeeID);
                                                        if (isSync || creatingSchedule || isManager || ![planWorksheetStatusMapping.ForSubmission, planWorksheetStatusMapping.UpdateRequired].includes(worksheet?.Status)) {
                                                            return;
                                                        };
                                                        setShowAddActivity(true)
                                                    }}
                                                    addLeaveClick={() => {
                                                        const isManager = Number(permission?.coverage?.employee_id) !== Number(worksheet?.EmployeeID);
                                                        if (isSync || creatingSchedule || isManager || ![planWorksheetStatusMapping.ForSubmission, planWorksheetStatusMapping.UpdateRequired].includes(worksheet?.Status)) {
                                                            return;
                                                        };
                                                        setShowAddLeave(true)
                                                    }}
                                                    addWorkWith={() => {
                                                        const isManager = Number(permission?.coverage?.employee_id) !== Number(worksheet?.EmployeeID);
                                                        if (isSync || creatingSchedule || isManager || ![planWorksheetStatusMapping.ForSubmission, planWorksheetStatusMapping.UpdateRequired].includes(worksheet?.Status)) {
                                                            return;
                                                        };
                                                        setShowAddWorkWith(true)
                                                    }}
                                                    handleRemove={onDeleteActivity}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </main>
                                <nav
                                    id="rightSidebar"
                                    className="col-2 d-md-block p-0 sidebar-layout collapse"
                                >
                                    <div className="position-sticky w-100 h-100">
                                        <PlanningSidebar
                                            selectedItem={selectedItem}
                                            onDeleteItem={onDeleteItem}
                                            worksheet={worksheet}
                                            selectedDate={selectedDate ? selectedDate.toLocaleDateString('en-CA') : ""}
                                        />
                                    </div>
                                </nav>
                            </div>
                        )}
                    </Tab.Pane>
                </Tab.Content >
            </Tab.Container >
            <AddSchedule
                show={showAddSchedule}
                onHide={() => setShowAddSchedule(false)}
                account={selectedAccount}
                worksheet={worksheet}
                selectedDate={selectedDate}
                isCreating={creatingSchedule}
                onAdd={addAccountToDate}
                onAddMultiple={addAccountToMultipleDate} />
            <AddActivity
                show={showAddActivity}
                onHide={() => setShowAddActivity(false)}
                worksheet={worksheet}
                selectedDate={selectedDate}
                isCreating={creatingSchedule}
                onAdd={addActivityToDate} />
            <AddLeave
                show={showAddLeave}
                onHide={() => setShowAddLeave(false)}
                worksheet={worksheet}
                selectedDate={selectedDate}
                isCreating={creatingSchedule}
                onAdd={addLeaveToDate} />
            <AddWorkWithModal
                show={showAddWorkWith}
                onHide={() => setShowAddWorkWith(false)}
                worksheet={worksheet}
                selectedDate={selectedDate}
                workWithEmployees={workWithEmployees}
                isCreating={creatingSchedule}
                employeeID={permission?.coverage?.employee_id}
                onProceed={addWorkWith}
            />
            <OpalAlertModal
                show={showAlertModal}
                onHide={() => setShowAlertModal(false)}
                title={alertModalProps?.title}
                bodyText={alertModalProps?.bodyText}
                confirmButtonText={alertModalProps?.confirmButtonText}
                cancelButtonText={alertModalProps?.cancelButtonText}
                onConfirm={alertModalProps?.onConfirm}
                modalType={alertModalProps?.modalType}
                Icon={alertModalProps?.Icon}
            />
            <OpalAlertModal
                show={showLoadingModal}
                onHide={() => setShowLoadingModal(false)}
                modalType={"message"}
                Icon={"/Images/loader.svg"}
                backdrop={"static"}
                keyboard={false}
                hideFooter={true}
            />
            <SubmissionErrorModal show={showSubmissionErrorModal} onHide={() => setShowSubmissionErrorModal(false)} validationMes={validaionError} />
        </div >
    );
};

export default OpalPlanningLayout;
